import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Info } from 'lucide-react';
import { pct } from './stageColors';
import type { DviLearningDashboardSummary } from '@/hooks/useDviLearningDashboard';
import type { StageReadinessRow } from '@/hooks/useDviStageReadiness';

interface Props {
  summary: DviLearningDashboardSummary | null | undefined;
  rows: StageReadinessRow[];
}

function MetricCard({ label, value, tooltip }: { label: string; value: string; tooltip: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
          {label}
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">{tooltip}</TooltipContent>
          </Tooltip>
        </div>
        <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
      </CardContent>
    </Card>
  );
}

export function OcrEffectivenessPanel({ summary, rows }: Props) {
  if (!summary) return null;
  const lift = summary.ocr_lift_pct;
  const sortedRows = [...rows]
    .filter((r) => r.ocr_lift_component_pct != null)
    .sort((a, b) => (b.ocr_lift_component_pct ?? 0) - (a.ocr_lift_component_pct ?? 0))
    .slice(0, 12);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">OCR Effectiveness</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MetricCard
            label="Match w/ OCR"
            value={pct(summary.ocr_supported_match_score_avg)}
            tooltip="Avg(match_score) where media has OCR text ≥10 chars and ocr_confidence ≥ 0.5."
          />
          <MetricCard
            label="Match w/o OCR"
            value={pct(summary.ocr_unsupported_match_score_avg)}
            tooltip="Avg(match_score) where media is NOT OCR-supported."
          />
          <MetricCard
            label="OCR Lift"
            value={lift == null ? 'Unavailable' : `${lift.toFixed(1)}%`}
            tooltip="(supported - unsupported) / unsupported × 100. Positive = OCR helps; near zero = no signal; negative = noise."
          />
        </div>

        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">Per-component OCR lift (suppressed if &lt; 20 samples)</div>
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component / Condition</TableHead>
                  <TableHead>w/ OCR</TableHead>
                  <TableHead>w/o OCR</TableHead>
                  <TableHead>Lift</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-4">No components with sufficient samples for OCR lift.</TableCell></TableRow>
                )}
                {sortedRows.map((r) => (
                  <TableRow key={`${r.component}-${r.condition_type}`}>
                    <TableCell>
                      <div className="text-sm">{r.component}</div>
                      <div className="text-xs text-muted-foreground">{r.condition_type}</div>
                    </TableCell>
                    <TableCell className="text-sm">{pct(r.ocr_supported_component_agreement)}</TableCell>
                    <TableCell className="text-sm">{pct(r.ocr_unsupported_component_agreement)}</TableCell>
                    <TableCell className={`text-sm font-medium ${(r.ocr_lift_component_pct ?? 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                      {r.ocr_lift_component_pct?.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
